import React, { useState, useEffect, useRef } from "react";
import SideBar from "../../Components/Sidebar";
import "./Payment.css";
import Pageheader from "../Container/PageHeader/Pageheader";
import ToggleTypeSelector from "../../Components/ToggleTypeSelector";
import { PAYMENT_TYPE } from "../../utils/constant";
import "../../Components/PaymentTable/PaymentTable.css";
import kebab from "../../Assests/Kebab.png";
import kebabActive from "../../Assests/kebab menu.svg";
import statusPaid from "../../Assests/statusPaid.svg";
import statusFailed from "../../Assests/Fail.svg";
import statusPending from "../../Assests/waiting.svg";
import statusRefunded from "../../Assests/refund.svg";
import receiptIcon from "../../Assests/Receipt.svg";
import rebookIcon from "../../Assests/Loop.svg";
import { capitalizeNames } from "../../utils/nameUtils";
import issueIcon from "../../Assests/Report.svg";
import PaymentTable from "../../Components/PaymentTable/PaymentTable";
import { checkStripeAccount, getPaymentTransactions, getStripeExpressDashboardLink } from "../../Services/stripeApi";
import { getUserProfileDetails } from "../../Services/ShowsApi";
import Avatar from "../../Assests/DefAvatar.png";
import editIcon from "../../Assests/edit.svg";
import ChatModal from "../ChatModal";
import { useSelector } from "react-redux";
import Loader from "../../Components/Loader";
import { Pagination } from "@mui/material";

const getStripeStatusDisplay = (status) => {
    switch (status) {
        case 'active': return 'Active';
        case 'pending': return 'Pending';
        case 'disabled': return 'Disabled';
        case 'incomplete': return 'Incomplete';
        case 'not_connected': 
        default: return 'Not Connected';
    }
};

const getStripeStatusClass = (status) => {
    switch (status) {
        case 'active': return 'connected';
        case 'pending': return 'pending';
        case 'disabled': return 'disabled';
        case 'incomplete': return 'incomplete';
        case 'not_connected':
        default: return 'disconnected';
    }
};

const getPaymentReadinessText = (status) => {
    switch (status) {
        case 'active': return 'You are ready to receive payments';
        case 'pending': return 'Your Stripe account is being reviewed. Verification usually takes 2–7 business days.';
        case 'disabled': return 'Your Stripe account is disabled';
        case 'incomplete': return 'Complete your Stripe account setup';
        case 'not_connected':
        default: return 'Set up Stripe to receive payments';
    }
};

const PaymentHistory = () => {
    const value = useSelector((state) => state?.value);
    const [paymentType, setPaymentType] = useState('Sent Payments');
    const [activeKebab, setActiveKebab] = useState(null);
    const [hasStripeAccount, setHasStripeAccount] = useState(null);
    const [sentPaymentsData, setSentPaymentsData] = useState([]);
    const [receivedPaymentsData, setReceivedPaymentsData] = useState([]);
    const [isLoadingPayments, setIsLoadingPayments] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState({ sent: { totalPages: 1 }, received: { totalPages: 1 } });
    const rowsPerPage = 10;
    const popupRef = useRef();

    const handlesSelectPayment = (type) => {
        setPaymentType(type);
        setActiveKebab(null);
        setCurrentPage(1);
    }

    const checkUserStripeAccount = async () => {
        try {
            const userId = localStorage.getItem('user_id');
            if (userId) {
                const hasAccount = await checkStripeAccount(userId);
                console.log('User has Stripe account:', hasAccount);
                setHasStripeAccount(hasAccount);
            }
        } catch (error) {
            console.error('Error checking Stripe account:', error);
            setHasStripeAccount(false);
        }
    };

    const fetchPaymentData = async () => {
        try {
            setIsLoadingPayments(true);
            const userId = localStorage.getItem('user_id');
            if (userId) {
                const type = paymentType === 'Sent Payments' ? 'sent' : 'received';
                const data = await getPaymentTransactions(userId, currentPage, rowsPerPage, type);
                console.log('Payment data received:', data);

                const payments = (data?.data || []).filter(payment => {
                    const status = payment.Status?.toLowerCase() || payment.status?.toLowerCase() || '';
                    if (type === 'sent') return status !== 'pending';
                    return status !== 'pending' && status !== 'blocked';
                });

                if (type === 'sent') {
                    setSentPaymentsData(payments);
                } else {
                    setReceivedPaymentsData(payments);
                }

                if (data?.pagination) {
                    setPaginationInfo(prev => ({
                        ...prev,
                        [type]: {
                            totalCount: data.pagination.totalCount,
                            totalPages: data.pagination.totalPages
                        }
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching payment data:', error);
            if (paymentType === 'Sent Payments') {
                setSentPaymentsData([]);
            } else {
                setReceivedPaymentsData([]);
            }
        } finally {
            setIsLoadingPayments(false);
        }
    };

    const fetchUserInfo = async () => {
        try {
            const userId = localStorage.getItem('user_id');
            const [profileResult, stripeResult] = await Promise.all([
                getUserProfileDetails("UserID"),
                userId ? checkStripeAccount(userId) : null
            ]);
            
            let profileData = null;
            if (profileResult?.data?.body?.Data?.length > 0) {
                profileData = profileResult.data.body.Data[0];
            } else {
                profileData = JSON.parse(localStorage.getItem('user') || '{}');
            }
            
            setUserInfo({
                name: capitalizeNames(profileData.FirstName || '', profileData.LastName || '').fullName || 'User',
                email: profileData.Email || 'No email provided',
                photo: profileData.ProfileImageKey || Avatar,
                stripeStatus: getStripeStatusDisplay(stripeResult?.status),
                stripeId: stripeResult?.stripeAccountId || 'Not Available'
            });
            
            setHasStripeAccount(stripeResult);
        } catch (error) {
            console.error("Error fetching user info:", error);
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            setUserInfo({
                name: capitalizeNames(userData.FirstName || '', userData.LastName || '').fullName || 'User',
                email: userData.Email || 'No email provided',
                photo: userData.ProfileImageKey || Avatar,
                stripeStatus: getStripeStatusDisplay(null),
                stripeId: 'Not Available'
            });
            setHasStripeAccount({ status: 'not_connected' });
        }
    };

    const handleAccessStripeDashboard = async () => {
        if (!hasStripeAccount?.stripeAccountId) {
            console.error('No Stripe account ID available');
            return;
        }

        try {
            setIsLoadingDashboard(true);
            const response = await getStripeExpressDashboardLink(hasStripeAccount.stripeAccountId);
            
            if (response?.dashboardUrl) {
                window.open(response.dashboardUrl, '_blank');
            } else {
                console.error('No dashboard URL received');
            }
        } catch (error) {
            console.error('Error accessing Stripe dashboard:', error);
        } finally {
            setIsLoadingDashboard(false);
        }
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setActiveKebab(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [popupRef]);

    useEffect(() => {
        fetchPaymentData();
        fetchUserInfo();
    }, []);

    useEffect(() => {
        fetchPaymentData();
    }, [currentPage, paymentType]);

    useEffect(() => {
        if (paymentType === 'Received Payments') {
            checkUserStripeAccount();
        }
    }, [paymentType]);

    return (
        <>
            <div
                className='theme-bg clr-white height-100'
                style={{ display: "flex", overflow: 'hidden' }}
            >
                <SideBar />
                <div className="Page-main-container">
                    <Pageheader pageTitle="Payments" />
                    <div className="payment_container">
                        {userInfo && (
                            <div className="user_info_card">
                                <div className="user_info_left">
                                    <img src={userInfo.photo} alt="User" className="user_info_photo" />
                                    <div className="user_info_details">
                                        <div className="user_info_name_row">
                                            <div className="user_info_name">{userInfo.name}</div>
                                            <div className={`stripe_status_bubble ${getStripeStatusClass(hasStripeAccount?.status)}`}>
                                                {userInfo.stripeStatus}
                                            </div>
                                        </div>
                                        <div className="payment_readiness_text">
                                            {getPaymentReadinessText(hasStripeAccount?.status)}
                                        </div>
                                    </div>
                                </div>
                                <div className="user_info_right">
                                    <div className="user_info_fields">
                                        <div className="user_info_stripe_id">
                                            <span className="user_info_label">Stripe ID:</span>
                                            <span className="user_info_value">{userInfo.stripeId}</span>
                                        </div>
                                        <div className="user_info_email_row">
                                            <span className="user_info_label">Email:</span>
                                            <span className="user_info_value">{userInfo.email}</span>
                                        </div>
                                    </div>
                                    <div className="user_info_actions">
                                        {hasStripeAccount?.status === 'active' && (
                                            <button 
                                                className="stripe_edit_button"
                                                onClick={handleAccessStripeDashboard}
                                                disabled={isLoadingDashboard}
                                            >
                                                <img src={editIcon} alt="Edit" className="edit_icon" />
                                                {isLoadingDashboard ? 'Loading...' : 'Edit'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="payment_toggle_container">
                            <ToggleTypeSelector
                                TYPE_Items={PAYMENT_TYPE}
                                toggleType={paymentType}
                                handleSearchTypeChange={handlesSelectPayment}
                            />
                        </div>
                        {(() => {
                            const data = paymentType === 'Sent Payments' ? sentPaymentsData : receivedPaymentsData;
                            const hasData = data && data.length > 0;
                            const showEmptyTable = paymentType === 'Received Payments' && hasStripeAccount?.status !== 'not_connected' && hasStripeAccount?.status !== 'incomplete';
                            const shouldUseTableClass = hasData || showEmptyTable;
                            const currentTotalPages = paymentType === 'Sent Payments' 
                                ? paginationInfo?.sent?.totalPages || 1 
                                : paginationInfo?.received?.totalPages || 1;
                            
                            return (
                                <>
                                <div className={shouldUseTableClass ? "payment_container_table_with_data" : "payment_container_table"}>
                                    {isLoadingPayments || (paymentType === 'Received Payments' && hasStripeAccount === null) ? (
                                        <Loader message="Loading payments..." />
                                    ) : (
                                        <PaymentTable
                                            data={data}
                                            activeKebab={activeKebab}
                                            setActiveKebab={setActiveKebab}
                                            popupRef={popupRef}
                                            kebab={kebab}
                                            kebabActive={kebabActive}
                                            statusPaid={statusPaid}
                                            statusFailed={statusFailed}
                                            statusPending={statusPending}
                                            statusRefunded={statusRefunded}
                                            receiptIcon={receiptIcon}
                                            rebookIcon={rebookIcon}
                                            issueIcon={issueIcon}
                                            paymentType={paymentType}
                                            showEmptyTable={showEmptyTable}
                                        />
                                    )}
                                </div>
                                {currentTotalPages > 1 && hasData && (
                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.8vh' }}>
                                        <Pagination
                                            count={currentTotalPages}
                                            page={currentPage}
                                            onChange={(event, value) => setCurrentPage(value)}
                                            sx={{
                                                "& .MuiPaginationItem-root": {
                                                    fontSize: "1.6vh",
                                                    minWidth: "3.2vh",
                                                    height: "3.2vh",
                                                    margin: "0 0.4vh",
                                                    borderRadius: "50%",
                                                    color: "#fff",
                                                },
                                                "& .MuiPaginationItem-root svg": {
                                                    fontSize: "2vh",
                                                },
                                                "& .MuiPaginationItem-root.Mui-selected": {
                                                    backgroundColor: "rgb(38 88 215)",
                                                },
                                                "& .MuiPaginationItem-root:hover": {
                                                    backgroundColor: "rgb(100, 100, 100)",
                                                },
                                            }}
                                        />
                                    </div>
                                )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
            <ChatModal open={value?.isChatOpen} />
        </>
    );
};

export default PaymentHistory;
